/**
 * RelationsBlock — the Relations section of the Inspector grammar
 * (design-system §5.4 / frontend-display §5). Renders typed links to the
 * entities a record points at (**References**) and the rows that point back at
 * it (**Referenced by**).
 *
 * E0: each relation is a *quiet row* (`h-9`, a HugeIcons type icon + label + the
 * target's title, hover `bg-accent rounded-md`) — never a bordered chip grid.
 * `divide-y` is opt-in (`dividers`) and only worth it for a long subsection.
 *
 * A relation either targets a single entity (`resource` + `id`, resolved to a
 * readable label via `RefLink`) or carries its own pre-rendered `node` (e.g. a
 * count, a back-reference list, a custom teaser). Subsections with zero items
 * render a calm muted note rather than vanishing, so the grammar is legible even
 * when a relation legitimately has none.
 */
import * as React from "react";
import { Link2, type IconComponent } from "@/components/ui/icon";

import { cn } from "@/lib/utils";
import { RefLink } from "@/components/inspector/RefLink";

export interface RelationItem {
  /** Stable key (defaults to `${resource}:${id}` when targeting an entity). */
  key?: string;
  /** Row label / relation role (e.g. "Parent Artifact", "Owner"). */
  label: string;
  /** HugeIcons-compatible icon for the relation type. Defaults to a generic link glyph. */
  icon?: IconComponent;
  /** Target resource path — when set with `id`, the row links via `RefLink`. */
  resource?: string;
  /** Target id to resolve + link to. */
  id?: string | number | null;
  /** Record column to read the link label from (passed to `RefLink`). */
  labelField?: string;
  /** Custom right-hand content, overriding the resource/id link. */
  node?: React.ReactNode;
}

export interface RelationGroup {
  /** Subsection heading: "References" | "Referenced by" | a custom label. */
  title: string;
  items: RelationItem[];
  /** Note shown when `items` is empty (defaults to a calm muted line). */
  emptyMessage?: string;
  /** Add `divide-y` hairlines between rows (only worth it when long). */
  dividers?: boolean;
}

function RelationRow({ item }: { item: RelationItem }) {
  const Icon = item.icon ?? Link2;
  return (
    <div className="flex h-9 items-center gap-2 rounded-md px-2 hover:bg-accent">
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="shrink-0 text-sm text-muted-foreground">{item.label}</span>
      <span className="ml-auto min-w-0 truncate text-right text-sm">
        {item.node !== undefined ? (
          item.node
        ) : item.resource ? (
          <RefLink
            resource={item.resource}
            id={item.id}
            labelField={item.labelField}
          />
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </span>
    </div>
  );
}

function Subsection({ group }: { group: RelationGroup }) {
  return (
    <div className="space-y-2">
      <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {group.title}
      </h4>
      {group.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {group.emptyMessage ?? "None."}
        </p>
      ) : (
        <div className={cn(group.dividers && "divide-y divide-border")}>
          {group.items.map((item, i) => (
            <RelationRow key={item.key ?? `${item.resource ?? "rel"}:${item.id ?? i}`} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

export interface RelationsBlockProps {
  /**
   * Relation subsections. Convention: a "References" group (out-links) and a
   * "Referenced by" group (back-references); any number of custom groups is
   * fine. Groups with empty `items` still render their heading + empty note.
   */
  groups: RelationGroup[];
  className?: string;
}

/**
 * Symmetric References / Referenced-by relation rows for a show page.
 */
export function RelationsBlock({ groups, className }: RelationsBlockProps) {
  if (groups.length === 0) {
    return (
      <p className={cn("text-sm text-muted-foreground", className)}>
        No relations.
      </p>
    );
  }
  return (
    <div className={cn("space-y-6", className)}>
      {groups.map((group) => (
        <Subsection key={group.title} group={group} />
      ))}
    </div>
  );
}

export default RelationsBlock;
